import React, { useState, useEffect } from 'react';
import { ModelReferenceUpdate, typesService } from '../../services/typesService';
import { clientService } from '../../services/clientService';
import { Client } from '../../types/client';
import { useI18n } from '../../contexts/I18nContext';
import './EditModelReferenceModal.css';

interface ModelRefWithHardware {
  id: number;
  code: string;
  name?: string;
  description?: string;
  is_active: boolean;
  client_ids?: string[];
  hardware_type_id: number;
  hardware_type_code: string;
  hardware_type_name: string;
  created_at: string;
  updated_at: string;
}

interface EditModelReferenceModalProps {
  isOpen: boolean;
  modelRef: ModelRefWithHardware | null;
  onClose: () => void;
  onUpdated: () => void;
  onUpdate: (id: number, data: ModelReferenceUpdate) => Promise<void>;
}

interface FormData {
  code: string;
  name: string;
  description: string;
  is_active: boolean;
}

interface FormErrors {
  [key: string]: string;
}

export const EditModelReferenceModal: React.FC<EditModelReferenceModalProps> = ({
  isOpen,
  modelRef,
  onClose,
  onUpdated,
  onUpdate
}) => {
  const { t } = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [formData, setFormData] = useState<FormData>({
    code: '',
    name: '',
    description: '',
    is_active: true
  });

  // Client management state
  const [clientIds, setClientIds] = useState<string[]>([]);
  const [allClients, setAllClients] = useState<Client[]>([]);
  const [selectedClientToAdd, setSelectedClientToAdd] = useState<string>('');
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [clientNames, setClientNames] = useState<Map<string, string>>(new Map());

  // Initialize form data when modelRef changes
  useEffect(() => {
    if (modelRef) {
      setFormData({
        code: modelRef.code || '',
        name: modelRef.name || '',
        description: modelRef.description || '',
        is_active: modelRef.is_active
      });
      setClientIds(modelRef.client_ids || ['00000000-0000-0000-0000-000000000000']);
      setErrors({});
    }
  }, [modelRef]);

  // Load all clients for the dropdown
  useEffect(() => {
    const loadClients = async () => {
      if (!isOpen) return;
      try {
        const clients = await clientService.fetchActiveClients();
        setAllClients(clients);
        const namesMap = new Map<string, string>();
        clients.forEach(c => namesMap.set(c.id, c.name));
        setClientNames(namesMap);
      } catch (err) {
        console.error('Failed to load clients:', err);
      }
    };
    loadClients();
  }, [isOpen]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.code.trim()) {
      newErrors.code = 'Reference code is required';
    } else if (!/^[A-Z0-9-]{2,20}$/.test(formData.code)) {
      newErrors.code = 'Code must be 2-20 uppercase letters, numbers, or dashes';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof FormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !modelRef) return;

    setIsSubmitting(true);
    try {
      await onUpdate(modelRef.id, {
        code: formData.code.trim(),
        name: formData.name.trim() || undefined,
        description: formData.description.trim() || undefined,
        is_active: formData.is_active
      });
      onUpdated();
      onClose();
    } catch (error: any) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update model reference';
      setErrors({ submit: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  // Client management handlers
  const getClientName = (clientId: string): string => {
    if (clientId === '00000000-0000-0000-0000-000000000000') {
      return 'Global';
    }
    return clientNames.get(clientId) || clientId;
  };

  const handleAddClient = async () => {
    if (!selectedClientToAdd || !modelRef) return;
    setIsLoadingClients(true);
    setErrors(prev => ({ ...prev, clients: '' }));
    try {
      const result = await typesService.addModelReferenceClient(modelRef.id, selectedClientToAdd);
      setClientIds(result.client_ids);
      setSelectedClientToAdd('');
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to add client';
      setErrors(prev => ({ ...prev, clients: errorMessage }));
    } finally {
      setIsLoadingClients(false);
    }
  };

  const handleRemoveClient = async (clientIdToRemove: string) => {
    if (!modelRef) return;
    if (clientIds.length <= 1) {
      setErrors(prev => ({ ...prev, clients: 'Cannot remove the last client. At least one client must remain.' }));
      return;
    }
    setIsLoadingClients(true);
    setErrors(prev => ({ ...prev, clients: '' }));
    try {
      const result = await typesService.removeModelReferenceClient(modelRef.id, clientIdToRemove);
      setClientIds(result.client_ids);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to remove client';
      setErrors(prev => ({ ...prev, clients: errorMessage }));
    } finally {
      setIsLoadingClients(false);
    }
  };

  // Get clients that can be added (not already associated)
  const availableClientsToAdd = allClients.filter(
    c => !clientIds.some(id => id.replace(/-/g, '').toLowerCase() === c.id.replace(/-/g, '').toLowerCase())
  );

  if (!isOpen || !modelRef) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="edit-model-ref-modal" data-testid="modal-edit-model-reference" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="modal-header">
          <div className="header-title-section">
            <div className="header-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </div>
            <h2>{t('deviceTypes.editModelReference')}</h2>
          </div>
          <button className="close-button" data-testid="btn-modal-close" onClick={handleClose} aria-label={t('common.close')}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Error Banner */}
        {errors.submit && (
          <div className="error-banner">
            <div className="error-content">
              <span className="error-icon">!</span>
              <span>{errors.submit}</span>
            </div>
            <button onClick={() => setErrors(prev => ({ ...prev, submit: '' }))}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="modal-content">
            {/* Section: Hardware Type (Read-only) */}
            <div className="form-section">
              <h3 className="section-title">{t('deviceTypes.hardwareTypes').slice(0, -1)}</h3>
              <p className="section-description">
                The hardware type this model reference belongs to.
              </p>

              <div className="form-fields">
                <div className="form-field">
                  <label>{t('deviceTypes.hardwareTypes').slice(0, -1)}</label>
                  <input
                    type="text"
                    value={`${modelRef.hardware_type_name} (${modelRef.hardware_type_code})`}
                    className="field-input"
                    disabled
                  />
                  <span className="field-hint">{t('deviceTypes.hardwareTypeCannotChange')}</span>
                </div>
              </div>
            </div>

            {/* Section: Reference Details */}
            <div className="form-section">
              <h3 className="section-title">{t('deviceTypes.referenceDetails')}</h3>
              <p className="section-description">
                Update the model reference code and name for this hardware revision.
              </p>

              <div className="form-fields">
                <div className="form-field">
                  <label className="required">{t('deviceTypes.referenceCode')}</label>
                  <input
                    type="text"
                    data-testid="field-modelref-code-edit"
                    value={formData.code}
                    onChange={(e) => handleInputChange('code', e.target.value.toUpperCase())}
                    className={`field-input ${errors.code ? 'error' : ''}`}
                    placeholder="e.g., PGA-04, IOTL-V2"
                    maxLength={20}
                    disabled={isSubmitting}
                  />
                  <span className="field-hint">Uppercase letters, numbers, and dashes (2-20 chars)</span>
                  {errors.code && <span className="field-error" data-testid="error-modelref-code">{errors.code}</span>}
                </div>

                <div className="form-field">
                  <label>{t('deviceTypes.name')}</label>
                  <input
                    type="text"
                    data-testid="field-modelref-name-edit"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="field-input"
                    placeholder="e.g., PGA Gateway Rev 4"
                    disabled={isSubmitting}
                  />
                  <span className="field-hint">Human-readable name (optional)</span>
                </div>

                <div className="form-field full-width">
                  <label>{t('common.description')}</label>
                  <textarea
                    data-testid="field-modelref-description-edit"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    className="field-textarea"
                    placeholder={t('deviceTypes.modelRefDescriptionPlaceholder')}
                    rows={3}
                    disabled={isSubmitting}
                  />
                  <span className="field-hint">Optional - describe the hardware revision or variant</span>
                </div>
              </div>
            </div>

            {/* Section: Status */}
            <div className="form-section">
              <h3 className="section-title">{t('clients.status')}</h3>
              <p className="section-description">
                Control whether this model reference is active and available for use.
              </p>

              <div className="form-fields">
                <div className="form-field">
                  <label>{t('clients.status')}</label>
                  <select
                    data-testid="field-modelref-is-active"
                    value={formData.is_active ? 'active' : 'inactive'}
                    onChange={(e) => handleInputChange('is_active', e.target.value === 'active')}
                    className="field-select"
                    disabled={isSubmitting}
                  >
                    <option value="active">{t('common.active')}</option>
                    <option value="inactive">{t('common.inactive')}</option>
                  </select>
                  <span className="field-hint">Inactive references won't appear in device creation</span>
                </div>
              </div>
            </div>

            {/* Section: Clients */}
            <div className="form-section">
              <h3 className="section-title">{t('deviceTypes.associatedClients')}</h3>
              <p className="section-description">
                Control which clients can access this model reference. At least one client must remain.
              </p>

              {errors.clients && (
                <div className="error-banner" style={{ marginBottom: '12px' }}>
                  <div className="error-content">
                    <span className="error-icon">!</span>
                    <span>{errors.clients}</span>
                  </div>
                  <button type="button" onClick={() => setErrors(prev => ({ ...prev, clients: '' }))}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}

              <div className="clients-list-container">
                {clientIds.length > 0 ? (
                  <ul className="clients-list">
                    {clientIds.map((clientId) => {
                      const displayName = getClientName(clientId);
                      const isGlobal = clientId === '00000000-0000-0000-0000-000000000000';
                      return (
                        <li key={clientId} className="client-item">
                          <div className="client-info">
                            <span className="client-name">
                              {displayName}
                              {isGlobal && <span className="global-badge">Global</span>}
                            </span>
                          </div>
                          {clientIds.length > 1 && (
                            <button
                              type="button"
                              className="btn btn-danger btn-small"
                              onClick={() => handleRemoveClient(clientId)}
                              disabled={isLoadingClients}
                            >
                              Remove
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="no-clients">No clients associated.</p>
                )}
              </div>

              <div className="add-client-row">
                <select
                  value={selectedClientToAdd}
                  onChange={(e) => setSelectedClientToAdd(e.target.value)}
                  className="field-select"
                  disabled={isLoadingClients}
                >
                  <option value="">Add a client...</option>
                  {availableClientsToAdd.map(client => (
                    <option key={client.id} value={client.id}>{client.name}</option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleAddClient}
                  disabled={isLoadingClients || !selectedClientToAdd}
                >
                  {isLoadingClients ? 'Adding...' : 'Add'}
                </button>
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="modal-footer">
            <div className="footer-left">
              <button
                type="button"
                data-testid="btn-modelref-cancel"
                onClick={handleClose}
                className="btn btn-secondary"
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
            <div className="footer-right">
              <button
                type="submit"
                data-testid="btn-modelref-save"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner"></span>
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
